import { TextField, Box, Flex, Text, Link } from "@radix-ui/themes";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { useSurveyStore } from "@store/survey";
import { useState, useEffect } from "react";
import { getSchools } from "@api/data";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import maplibregl from "maplibre-gl";
import { createRoot } from "react-dom/client";
import { Trans } from "@lingui/macro";
import { useLingui, I18nProvider } from "@lingui/react";
import MapPopover from "@components/MapPopover";
import SchoolSummary from "@components/SchoolSummary";

export default function SchoolSearch() {
  const { setSchools, setSelectedSchool } = useSurveyStore();
  const mapRef = useSurveyStore((state) => state.getMapRef());
  const { i18n } = useLingui();
  const [searchResults, setSearchresults] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const appendMapMarker = useSurveyStore((state) => state.appendMapMarker);
  const appendMapComponentRef = useSurveyStore(
    (state) => state.appendMapComponentRef,
  );

  const { isPending, isError, data, error, isFetching } = useQuery({
    queryKey: ["schools", getSchools],
    queryFn: () => getSchools(),
    placeholderData: keepPreviousData,
    staleTime: 180000,
  });

  useEffect(() => {
    if (data) {
      setSchools(data);
    }
  }, [data, setSchools]);

  function handleSearch(event) {
    setSearchValue(event.target.value);

    if (isError) {
      console.error("Error fetching schools", error);
      return;
    }

    if (isPending || isFetching) return;

    if (event.target.value === "") {
      setSearchresults([]);
      return;
    }

    const results = data.reduce((acc, current) => {
      const x = acc.find(
        (item) =>
          item.properties &&
          item.properties.school_id === current.properties.school_id,
      );
      if (
        !x &&
        current.properties.school_name
          .toLowerCase()
          .includes(event.target.value.toLowerCase())
      ) {
        return acc.concat([current]);
      } else {
        return acc;
      }
    }, []);
    setSearchresults(results);
  }

  function onSchoolSelection(event, result) {
    event.preventDefault();

    const existingMarkers = useSurveyStore.getState().mapMarkers;
    const existingRefs = useSurveyStore.getState().mapComponentRefs;

    existingMarkers
      .filter((marker) => marker.getElement().id?.startsWith("summary-"))
      .forEach((marker) => marker.remove());

    existingRefs
      .filter((ref) =>
        ref?._internalRoot?.containerInfo?.id?.startsWith("summary-"),
      )
      .forEach((ref) => {
        ref.unmount();
      });

    useSurveyStore.setState({
      mapMarkers: existingMarkers.filter(
        (marker) => !marker.getElement().id?.startsWith("summary-"),
      ),
      mapComponentRefs: existingRefs.filter(
        (ref) =>
          !ref?._reactRootContainer?.containerInfo?.id?.startsWith("summary-"),
      ),
    });

    mapRef.jumpTo({
      center: result.geometry.coordinates,
      zoom: 15.5,
    });

    setSearchresults([]);
    setSelectedSchool(result);
    setSearchValue("");

    const el = document.createElement("div");
    el.id = `summary-${Math.random().toString(36).slice(2, 9)}`;
    el.addEventListener("click", (e) => e.stopPropagation(), false);
    const rootRef = createRoot(el);
    let marker = new maplibregl.Marker({ element: el });
    marker.setLngLat(result.geometry.coordinates).addTo(mapRef);

    rootRef.render(
      <I18nProvider i18n={i18n}>
        <MapPopover marker={marker} rootRef={rootRef}>
          <SchoolSummary />
        </MapPopover>
      </I18nProvider>,
    );

    appendMapMarker(marker);
    appendMapComponentRef(rootRef);
  }

  return (
    <div>
      <Box maxWidth="250px">
        <TextField.Root
          placeholder={i18n._({
            id: "SchoolSearch.search_for_school",
            message: "Search for a school",
          })}
          onChange={handleSearch}
          width="275px"
          value={searchValue}
        >
          <TextField.Slot>
            <MagnifyingGlassIcon height="16" width="16" />
          </TextField.Slot>
        </TextField.Root>
      </Box>
      {searchValue && (
        <Box pt="3" pb="2">
          {searchResults.length > 0 && (
            <Text size="1" color="gray">
              <Trans id="SchoolSearch.results">Results</Trans>
            </Text>
          )}
          <Flex direction="column" gap="1" align="left" pt="1">
            {searchResults.length === 0 && (
              <Text size="1" variant="ghost" style={{ textAlign: "left" }}>
                <Trans id="SchoolSearch.no_results">No results found</Trans>
              </Text>
            )}
            {searchResults.slice(0, 10).map((result, index) => (
              <Link
                highContrast
                size="1"
                key={index}
                variant="ghost"
                style={{ textAlign: "left" }}
                href="#"
                onClick={(event) => onSchoolSelection(event, result)}
              >
                {result.properties.school_name}
              </Link>
            ))}
          </Flex>
        </Box>
      )}
    </div>
  );
}
